import React, { useState, useEffect } from 'react';

const SdrRecordForm = ({ record, teachers, students, selectedDate, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    teacher_name: '',
    department: '',
    event_code: '',
    leave_type: '',
    activity_description: '',
    detailed_reason: '',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    affected_lessons: [],
    substitute_assignments: []
  });
  const [errors, setErrors] = useState({});
  const [showLessonSelector, setShowLessonSelector] = useState(false);

  const departments = [
    { id: 'chinese', name: '中文科' },
    { id: 'english', name: '英文科' },
    { id: 'mathematics', name: '數學科' },
    { id: 'chemistry', name: '化學科' },
    { id: 'physics', name: '物理科' },
    { id: 'biology', name: '生物科' },
    { id: 'history', name: '歷史科' },
    { id: 'geography', name: '地理科' },
    { id: 'economics', name: '經濟科' },
    { id: 'music', name: '音樂科' },
    { id: 'pe', name: '體育科' },
    { id: 'va', name: '視覺藝術科' },
    { id: 'ict', name: '資訊科技科' },
    { id: 'ls', name: '通識教育科' },
    { id: 'support', name: '學生支援組' }
  ];

  const leaveTypes = [
    { id: 'training', name: '進修培訓' },
    { id: 'business', name: '公幹' },
    { id: 'personal', name: '事假' },
    { id: 'sick', name: '病假' },
    { id: 'meeting', name: '會議' }
  ];

  const lessonPeriods = [
    { id: 1, name: '第1節', time: '08:30-09:15' },
    { id: 2, name: '第2節', time: '09:15-10:00' },
    { id: 3, name: '第3節', time: '10:15-11:00' },
    { id: 4, name: '第4節', time: '11:00-11:45' },
    { id: 5, name: '第5節', time: '12:30-13:15' },
    { id: 6, name: '第6節', time: '13:15-14:00' },
    { id: 7, name: '第7節', time: '14:00-14:45' },
    { id: 8, name: '第8節', time: '14:45-15:30' }
  ];

  const subjects = ['中文', '英文', '數學', '化學', '物理', '生物', '歷史', '地理', '經濟', '音樂', '體育', '視覺藝術', '資訊科技', '通識教育'];
  const classes = ['4A', '4B', '5A', '5B', '6A', '6B'];

  useEffect(() => {
    if (record) {
      setFormData({
        teacher_name: record.teacher_name || '',
        department: record.department || '',
        event_code: record.event_code || '',
        leave_type: record.leave_type || '',
        activity_description: record.activity_description || '',
        detailed_reason: record.detailed_reason || '',
        start_date: record.start_date || '',
        end_date: record.end_date || '',
        start_time: record.start_time || '09:00',
        end_time: record.end_time || '17:00',
        affected_lessons: record.affected_lessons || [],
        substitute_assignments: record.substitute_assignments || []
      });
    } else if (selectedDate) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        start_date: dateStr,
        end_date: dateStr
      }));
    }
  }, [record, selectedDate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleLessonChange = (lessonId, checked) => {
    if (checked) {
      // Add lesson
      const lesson = lessonPeriods.find(l => l.id === lessonId);
      const subject = subjects[Math.floor(Math.random() * subjects.length)];
      const class_name = classes[Math.floor(Math.random() * classes.length)];
      const student_count = Math.floor(Math.random() * 20) + 20;
      const classroom = `Room ${Math.floor(Math.random() * 20) + 1}`;
      const substitute_teacher = teachers[Math.floor(Math.random() * teachers.length)]?.teacher_name || '待安排';

      const newLesson = {
        period_id: lesson.id,
        period_name: lesson.name,
        time: lesson.time,
        subject: subject,
        class_name: class_name,
        student_count: student_count,
        classroom: classroom,
        substitute_teacher: substitute_teacher,
        status: 'pending'
      };

      setFormData(prev => ({
        ...prev,
        affected_lessons: [...prev.affected_lessons, newLesson]
      }));
    } else {
      // Remove lesson
      setFormData(prev => ({
        ...prev,
        affected_lessons: prev.affected_lessons.filter(lesson => lesson.period_id !== lessonId)
      }));
    }
  };

  const handleSelectAllLessons = () => {
    const allLessons = lessonPeriods.map(lesson => {
      const subject = subjects[Math.floor(Math.random() * subjects.length)];
      const class_name = classes[Math.floor(Math.random() * classes.length)];
      const student_count = Math.floor(Math.random() * 20) + 20;
      const classroom = `Room ${Math.floor(Math.random() * 20) + 1}`;
      const substitute_teacher = teachers[Math.floor(Math.random() * teachers.length)]?.teacher_name || '待安排';

      return {
        period_id: lesson.id,
        period_name: lesson.name,
        time: lesson.time,
        subject: subject,
        class_name: class_name,
        student_count: student_count,
        classroom: classroom,
        substitute_teacher: substitute_teacher,
        status: 'pending'
      };
    });

    setFormData(prev => ({
      ...prev,
      affected_lessons: allLessons
    }));
  };

  const handleClearAllLessons = () => {
    setFormData(prev => ({
      ...prev,
      affected_lessons: []
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.teacher_name.trim()) {
      newErrors.teacher_name = '教師姓名為必填項目';
    }

    if (!formData.department) {
      newErrors.department = '所屬部門為必填項目';
    }

    if (!formData.leave_type) {
      newErrors.leave_type = '請假類型為必填項目';
    }

    if (!formData.activity_description.trim()) {
      newErrors.activity_description = '活動描述為必填項目';
    }

    if (!formData.start_date) {
      newErrors.start_date = '開始日期為必填項目';
    }

    if (!formData.end_date) {
      newErrors.end_date = '結束日期為必填項目';
    }

    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      newErrors.end_date = '結束日期必須晚於或等於開始日期';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const recordData = {
        ...formData,
        substitute_assignments: formData.affected_lessons.map(lesson => ({
          lesson_id: lesson.period_id,
          substitute_teacher: lesson.substitute_teacher,
          status: lesson.status
        }))
      };
      onSave(recordData);
    }
  };

  const getImpactStats = () => {
    const totalLessons = formData.affected_lessons.length;
    const totalStudents = formData.affected_lessons.reduce((sum, lesson) => sum + (lesson.student_count || 0), 0);
    const uniqueClasses = new Set(formData.affected_lessons.map(lesson => lesson.class_name)).size;
    
    return { totalLessons, totalStudents, uniqueClasses };
  };

  const stats = getImpactStats();

  return (
    <div className="sdr-record-form">
      <div className="form-header">
        <h2>{record ? '編輯 SDR 記錄' : '新增 SDR 記錄'}</h2>
        <button className="close-btn" onClick={onCancel}>×</button>
      </div>

      <form onSubmit={handleSubmit} className="sdr-form-content">
        {/* 基本資訊區塊 */}
        <div className="form-section">
          <h3>基本資訊</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label className="required">教師姓名</label>
              <input
                type="text"
                value={formData.teacher_name}
                onChange={(e) => handleInputChange('teacher_name', e.target.value)}
                placeholder="請輸入教師姓名"
                className={errors.teacher_name ? 'error' : ''}
              />
              {errors.teacher_name && <span className="error-message">{errors.teacher_name}</span>}
            </div>

            <div className="form-group">
              <label className="required">所屬部門</label>
              <select
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                className={errors.department ? 'error' : ''}
              >
                <option value="">請選擇部門</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.name}>
                    {dept.name}
                  </option>
                ))}
              </select>
              {errors.department && <span className="error-message">{errors.department}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>事件代碼</label>
              <input
                type="text"
                value={formData.event_code}
                onChange={(e) => handleInputChange('event_code', e.target.value)}
                placeholder="選填，用於分類管理"
              />
            </div>

            <div className="form-group">
              <label className="required">請假類型</label>
              <select
                value={formData.leave_type}
                onChange={(e) => handleInputChange('leave_type', e.target.value)}
                className={errors.leave_type ? 'error' : ''}
              >
                <option value="">請選擇請假類型</option>
                {leaveTypes.map(type => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.leave_type && <span className="error-message">{errors.leave_type}</span>}
            </div>
          </div>
        </div>

        {/* 活動詳情區塊 */}
        <div className="form-section">
          <h3>活動詳情</h3>
          
          <div className="form-group">
            <label className="required">活動描述</label>
            <textarea
              value={formData.activity_description}
              onChange={(e) => handleInputChange('activity_description', e.target.value)}
              placeholder="請詳細說明活動內容、地點、目的"
              rows={3}
              className={errors.activity_description ? 'error' : ''}
            />
            {errors.activity_description && <span className="error-message">{errors.activity_description}</span>}
          </div>

          <div className="form-group">
            <label>詳細原因</label>
            <textarea
              value={formData.detailed_reason}
              onChange={(e) => handleInputChange('detailed_reason', e.target.value)}
              placeholder="選填，補充說明離校原因"
              rows={2}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="required">開始日期</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className={errors.start_date ? 'error' : ''}
              />
              {errors.start_date && <span className="error-message">{errors.start_date}</span>}
            </div>

            <div className="form-group">
              <label className="required">結束日期</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className={errors.end_date ? 'error' : ''}
              />
              {errors.end_date && <span className="error-message">{errors.end_date}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>開始時間</label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => handleInputChange('start_time', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>結束時間</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => handleInputChange('end_time', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* 課節影響管理區塊 */}
        <div className="form-section">
          <h3>課節影響管理</h3>
          
          <div className="lesson-selection-header">
            <div className="selection-controls">
              <button type="button" onClick={handleSelectAllLessons} className="control-btn">
                全選
              </button>
              <button type="button" onClick={handleClearAllLessons} className="control-btn">
                清除
              </button>
            </div>
            
            <button 
              type="button"
              className="select-lessons-btn"
              onClick={() => setShowLessonSelector(true)}
            >
              選擇受影響課節 ({formData.affected_lessons.length}節已選)
            </button>
          </div>

          {formData.affected_lessons.length > 0 && (
            <div className="affected-lessons">
              <h4>已選課節詳情</h4>
              <div className="lessons-grid">
                {formData.affected_lessons.map((lesson, index) => (
                  <div key={index} className="lesson-card">
                    <div className="lesson-header">
                      <span className="lesson-period">{lesson.period_name}</span>
                      <span className="lesson-time">{lesson.time}</span>
                    </div>
                    <div className="lesson-details">
                      <div className="detail-item">
                        <span className="label">科目:</span>
                        <span className="value">{lesson.subject}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">班級:</span>
                        <span className="value">{lesson.class_name}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">學生數:</span>
                        <span className="value">{lesson.student_count}人</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">教室:</span>
                        <span className="value">{lesson.classroom}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">代課老師:</span>
                        <span className="value">{lesson.substitute_teacher}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="impact-stats">
            <h4>影響統計</h4>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">{stats.totalLessons}</span>
                <span className="stat-label">影響課節數</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{stats.totalStudents}</span>
                <span className="stat-label">影響學生人數</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{stats.uniqueClasses}</span>
                <span className="stat-label">涉及班級數</span>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>
            取消
          </button>
          <button type="submit" className="save-btn">
            💾 儲存 SDR 記錄
          </button>
        </div>
      </form>

      {/* Lesson Selector Modal */}
      {showLessonSelector && (
        <LessonSelector
          lessonPeriods={lessonPeriods}
          selectedLessons={formData.affected_lessons.map(l => l.period_id)}
          onLessonChange={handleLessonChange}
          onClose={() => setShowLessonSelector(false)}
        />
      )}
    </div>
  );
};

// Lesson Selector Component
const LessonSelector = ({ lessonPeriods, selectedLessons, onLessonChange, onClose }) => {
  return (
    <div className="lesson-selector-overlay">
      <div className="lesson-selector">
        <div className="selector-header">
          <h3>選擇受影響課節</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="lessons-grid">
          {lessonPeriods.map(period => (
            <label key={period.id} className="lesson-option">
              <input
                type="checkbox"
                checked={selectedLessons.includes(period.id)}
                onChange={(e) => onLessonChange(period.id, e.target.checked)}
              />
              <div className="lesson-info">
                <span className="period-name">{period.name}</span>
                <span className="period-time">{period.time}</span>
              </div>
            </label>
          ))}
        </div>

        <div className="selector-footer">
          <div className="selection-count">
            已選擇 {selectedLessons.length} 個課節
          </div>
          <button className="confirm-btn" onClick={onClose}>
            確認選擇
          </button>
        </div>
      </div>
    </div>
  );
};

export default SdrRecordForm;
